// @flow
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import withGroupClass from '@hoc/withGroupClass';
import InterventionsLayout from './InterventionsLayout';
import InterventionsList from './InterventionsList';
import InterventionsPeriodFilter from './InterventionsPeriodFilter';
import Toast from '_common/services/Toast/Toast';

import type { GroupClassDetailed } from '@groupClass/types';
import { ADMIN_HEADER_INTERVENTIONS } from '@layout/constants';
import AdminLayout from '@layout/components/AdminLayout';
import InterventionRegistrationService from '@intervention/services/InterventionRegistrationService';
import RegisteredsService from '@groupClass/services/RegisteredsService';
import InterventionsService from '@root/src/Intervention/services/InterventionsService';
import type { InterventionType } from '@root/src/Intervention/types';
import type { InterventionRegistration, InterventionRegistrationType, InterventionsServiceData } from '@root/src/Intervention/types';
import InterventionRegistrations from '@root/src/Intervention/components/InterventionRegistrations';
import { PERSON_TYPE_BENEFICIARY, PERSON_TYPE_PARTICIPANT } from '@person/constants';
import {
  INTERVENTION_REGISTRATION_TYPE_PRESENT,
  INTERVENTION_REGISTRATION_TYPE_SCHEDULED,
} from '@root/src/Intervention/constants';

type InterventionsProps = {
  groupClass: GroupClassDetailed | null,
};

export const Interventions = (props: InterventionsProps): React$Node => {
  const { groupClass } = props;
  const { t, i18n: { language } } = useTranslation();

  const getRegistrationType = useCallback((registration: InterventionRegistration): string | null => {
    if (registration.personType) return registration.personType;
    if (registration.beneficiaryId !== null && registration.beneficiaryId !== undefined) return PERSON_TYPE_BENEFICIARY;
    if (registration.participantId !== null && registration.participantId !== undefined) return PERSON_TYPE_PARTICIPANT;
    return null;
  }, []);

  const [ interventions, setInterventions ] = useState<InterventionType[]>(InterventionsService.interventionsValue || []);
  const [ selectedInterventionId, setSelectedInterventionId ]
    = useState<number>(0);

  const [ interventionRegistrations, setInterventionRegistrations ] = useState<InterventionRegistration[]>([]);
  const [ initialRegistrations, setInitialRegistrations ] = useState<InterventionRegistration[]>([]);
  const [ isSaving, setIsSaving ] = useState<boolean>(false);
  const [ selectedPeriodId, setSelectedPeriodId ] = useState<number | null>(null);

  const periods = groupClass?.periods;

  const filteredInterventions = useMemo((): InterventionType[] => {
    if (!selectedPeriodId) return interventions;

    const selectedPeriod = periods?.find((period) => period.id === selectedPeriodId) || null;
    if (!selectedPeriod) return interventions;

    const periodStart = new Date(selectedPeriod.startDate);
    const periodEnd = new Date(selectedPeriod.endDate);

    return interventions.filter((intervention) => (
      intervention.startDate >= periodStart
      && intervention.endDate <= periodEnd
    ));
  }, [interventions, periods, selectedPeriodId]);

  const findCurrentInterventionId = (
    interventions: InterventionType[],
  ): number | null => {
    const now = new Date();

    const current = interventions.find((intervention) => {
      const start = new Date(intervention.startDate);
      const end = new Date(intervention.endDate);

      return start <= now && now <= end;
    });

    return current ? current.id : null;
  };

  const selectedIntervention = useMemo<InterventionType | null>(() => (
    filteredInterventions.find(
      (intervention) => intervention.id === selectedInterventionId,
    ) || null
  ), [filteredInterventions, selectedInterventionId]);

  const handleSelectIntervention = useCallback((interventionId: number): void => {
    setSelectedInterventionId(interventionId);

    // scroll to the top once click on intervention
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, []);

  const handleChangePeriod = useCallback((periodId: number | null): void => {
    setSelectedPeriodId(periodId);
  }, []);

  const handleChangeParticipantStatus = useCallback((
    personId: number,
    status: InterventionRegistrationType,
  ): void => {
    setInterventionRegistrations((prev) => (
      prev.map((registration) => (
        (registration.beneficiaryId ?? registration.participantId ?? registration.id) === personId
          ? { ...registration, status }
          : registration
      ))
    ));
  }, []);

  const handleMarkAllPresent = useCallback((): void => {
    setInterventionRegistrations((prev) => (
      prev.map((registration) => ({
        ...registration,
        status: INTERVENTION_REGISTRATION_TYPE_PRESENT,
      }))
    ));
  }, []);

  const handleCancel = useCallback((): void => {
    setInterventionRegistrations(initialRegistrations);
  }, [initialRegistrations]);

  const hasUnsavedChanges = useMemo((): boolean => {
    if (initialRegistrations.length === 0 && interventionRegistrations.length === 0) return false;
    if (initialRegistrations.length !== interventionRegistrations.length) return true;

    const initialStatusByKey: { [string]: InterventionRegistrationType } = initialRegistrations.reduce((acc, registration) => {
      const personId = registration.beneficiaryId ?? registration.participantId;
      acc[String(personId)] = registration.status;
      return acc;
    }, ({}: { [string]: InterventionRegistrationType }));

    return interventionRegistrations.some(
      (registration) => {
        const personId = registration.beneficiaryId ?? registration.participantId;
        return initialStatusByKey[String(personId)] !== registration.status;
      },
    );
  }, [initialRegistrations, interventionRegistrations]);

  const handleSave = useCallback((): void => {
    if (!selectedInterventionId) return;

    const initialStatusByKey: { [string]: InterventionRegistrationType } = initialRegistrations.reduce((acc, registration) => {
      const personId = registration.beneficiaryId ?? registration.participantId;
      acc[String(personId)] = registration.status;
      return acc;
    }, ({}: { [string]: InterventionRegistrationType }));

    const changes = interventionRegistrations
      .filter((registration) => {
        const personId = registration.beneficiaryId ?? registration.participantId;
        return initialStatusByKey[String(personId)] !== registration.status;
      })
      .map((registration) => {
        const personType = getRegistrationType(registration);
        let beneficiaryId = null;
        let participantId = null;

        if (personType === PERSON_TYPE_BENEFICIARY) {
          beneficiaryId = registration.beneficiaryId;
        } else if (personType === PERSON_TYPE_PARTICIPANT) {
          participantId = registration.participantId;
        } else if (registration.beneficiaryId !== null && registration.beneficiaryId !== undefined) {
          beneficiaryId = registration.beneficiaryId;
        } else if (registration.participantId !== null && registration.participantId !== undefined) {
          participantId = registration.participantId;
        }

        return {
          beneficiaryId,
          participantId,
          status: registration.status,
        };
      });

    if (changes.length === 0) return;

    setIsSaving(true);
    InterventionRegistrationService.updateStatuses(selectedInterventionId, language, changes)
      .then((updated) => {
        const updatedStatusByKey: { [string]: InterventionRegistrationType } = updated.reduce((acc, registration) => {
          const personId = registration.beneficiaryId ?? registration.participantId;
          acc[String(personId)] = registration.status;
          return acc;
        }, ({}: { [string]: InterventionRegistrationType }));

        const mergedRegistrations = interventionRegistrations.map((registration) => {
          const personId = registration.beneficiaryId ?? registration.participantId;
          const updatedStatus = updatedStatusByKey[String(personId)];
          return updatedStatus !== undefined
            ? { ...registration, status: updatedStatus }
            : registration;
        });

        setInterventionRegistrations(mergedRegistrations);
        setInitialRegistrations(mergedRegistrations);
        Toast.success(t('groupClasses.groupClass.peoples.form.wellUpdated'));
      })
      .catch(() => {
        Toast.error(t('groupClasses.groupClass.peoples.form.genericError'));
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [selectedInterventionId, language, initialRegistrations, interventionRegistrations, t, getRegistrationType]);

  useEffect(() => {
    const handleUpdate = (data: InterventionsServiceData): void => {
      setInterventions(data.interventions);
    };

    return InterventionsService.onChange(handleUpdate);
  }, []);

  useEffect(() => {
    if (groupClass?.id) {
      InterventionsService.fetchAll(groupClass.id, language);
    }
  }, [groupClass, language]);

  useEffect(() => {
    if (filteredInterventions.length === 0) {
      if (selectedInterventionId) setSelectedInterventionId(0);
      return;
    }

    const isSelectedVisible = filteredInterventions.some((intervention) => intervention.id === selectedInterventionId);
    if (!isSelectedVisible) setSelectedInterventionId(filteredInterventions[0].id);
  }, [filteredInterventions, selectedInterventionId]);

  useEffect(() => {
    if (selectedInterventionId) return;
    if (filteredInterventions.length === 0) return;

    const currentId = findCurrentInterventionId(filteredInterventions);

    setSelectedInterventionId(
      currentId ?? filteredInterventions[0].id,
    );
  }, [filteredInterventions, selectedInterventionId]);

  const fetchRegistrations = useCallback((interventionId: number): void => {
    if (!groupClass?.id) return;

    Promise.all([
      RegisteredsService.fetchGroupClassRegistereds(groupClass.id, language, {
        pagination: false,
        'order[person.lastName]': 'asc',
      }),
      InterventionRegistrationService.fetchAll(interventionId, language),
    ])
      .then(([groupClassRegistereds, registrations]) => {
        const registrationsByKey: { [string]: InterventionRegistration } = {};
        const interventionRegistrations: InterventionRegistration[] = [];
        registrations.forEach((registration) => {
          const personId = registration.beneficiaryId ?? registration.participantId;
          if (!personId) {
            interventionRegistrations.push({ ...registration, isShapeParticipant: false });
            return;
          }
          registrationsByKey[String(personId)] = { ...registration, isShapeParticipant: false };
        });

        // merge of group class registereds and intervention registrations
        groupClassRegistereds.forEach((registered) => {
          const personType = registered.person?.type || null;
          const personId = registered.person?.id ?? registered.id;
          const personKey = String(personId);
          const isShapeUser = registered.person?.shapeUser === true;
          const isBeneficiary = personType === PERSON_TYPE_BENEFICIARY;
          const isParticipant = personType === PERSON_TYPE_PARTICIPANT;
          const existingRegistration = registrationsByKey[personKey] || null;
          const beneficiaryId = isBeneficiary
            ? personId
            : (personType ? null : existingRegistration?.beneficiaryId ?? null);
          const participantId = isParticipant
            ? personId
            : (personType ? null : existingRegistration?.participantId ?? null);

          if (isShapeUser && !existingRegistration) {
            return;
          }

          if (existingRegistration) {
            registrationsByKey[personKey] = {
              ...existingRegistration,
              beneficiaryId,
              participantId,
              personType,
              firstName: registered.person?.firstName || existingRegistration.firstName,
              lastName: registered.person?.lastName || existingRegistration.lastName,
              isShapeParticipant: isShapeUser,
            };
            return;
          }

          registrationsByKey[personKey] = {
            id: registered.id,
            interventionId,
            beneficiaryId,
            participantId,
            personType,
            firstName: registered.person?.firstName || '',
            lastName: registered.person?.lastName || '',
            status: INTERVENTION_REGISTRATION_TYPE_SCHEDULED,
            registeredAt: null,
            isShapeParticipant: isShapeUser,
          };
        });

        const mergedRegistrations: InterventionRegistration[] = [
          ...Object.values(registrationsByKey),
          ...interventionRegistrations,
        ];
        const sortedRegistrations = mergedRegistrations.slice().sort((a, b) => {
          const aLastName = (a.lastName || '').toLowerCase();
          const bLastName = (b.lastName || '').toLowerCase();
          return aLastName.localeCompare(bLastName);
        });

        setInterventionRegistrations(sortedRegistrations);
        setInitialRegistrations(sortedRegistrations);
      });
  }, [groupClass, language]);

  useEffect(() => {
    if (selectedInterventionId) {
      fetchRegistrations(selectedInterventionId);
    }
  }, [selectedInterventionId, fetchRegistrations]);

  const participantsNode = useMemo((): React$Node => {
    if (filteredInterventions.length === 0 && selectedPeriodId !== null) {
      return (
        <div className="interventions-participants__empty">
          <p>{ t('groupClasses.groupClass.interventions.period.empty') }</p>
        </div>
      );
    }

    if (!selectedIntervention) return null;

    return (
      <InterventionRegistrations
        registrations={ interventionRegistrations }
        intervention={ selectedIntervention }
        onChangeStatus={ handleChangeParticipantStatus }
        onMarkAllPresent={ handleMarkAllPresent }
        onCancel={ handleCancel }
        onSave={ handleSave }
        isSaving={ isSaving }
        hasUnsavedChanges={ hasUnsavedChanges }
      />
    );
  }, [
    filteredInterventions.length,
    handleCancel,
    handleChangeParticipantStatus,
    handleMarkAllPresent,
    handleSave,
    hasUnsavedChanges,
    interventionRegistrations,
    isSaving,
    selectedIntervention,
    selectedPeriodId,
    t,
  ]);

  return (
    <AdminLayout groupClass={ groupClass } activeTab={ ADMIN_HEADER_INTERVENTIONS } preventPortraitMode>
      { interventions?.length > 0 ? (
        <InterventionsLayout>
          <InterventionsList
            interventions={ filteredInterventions }
            selectedInterventionId={ selectedInterventionId }
            onSelectIntervention={ handleSelectIntervention }
          />
          { participantsNode }
          <InterventionsPeriodFilter
            periods={ periods }
            selectedPeriodId={ selectedPeriodId }
            onChange={ handleChangePeriod }
          />
        </InterventionsLayout>
      ) : (
        <div className="interventions-participants__empty">
          <p>{ t('groupClasses.groupClass.interventions.empty') }</p>
        </div>
      )
      }
    </AdminLayout>
  );
};

export default withGroupClass(Interventions);
